import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Button, Card, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Checkbox, IconButton,
  TablePagination, alpha, Avatar, CircularProgress, Alert,
  Menu, MenuItem, Snackbar, Typography, useTheme, useMediaQuery
} from '@mui/material';
import { Plus, MoreVertical, Trash2, Edit, Tag } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAdminBrands, deleteAdminBrand, bulkDeleteAdminBrands, fetchCategoryOptions } from '../redux/slices/catalogSlice';
import BrandModal from '../components/BrandModal';
import PageHeader from '../components/common/PageHeader';
import FilterBar from '../components/common/FilterBar';
import StatusBadge from '../components/common/StatusBadge';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import axios from '../utils/axios';
import ExportTableButton from '../components/common/ExportTableButton';

const avatarColor = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return `hsl(${hash % 360}, 45%, 45%)`;
};

const BrandsView = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { data: brands, total, loading, error } = useSelector((state) => state.catalog.brands);
  // Full list for the parent-category dropdown — NOT the paginated Categories table state.
  const { data: categories } = useSelector((state) => state.catalog.categoryOptions);

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);

  // Modal & Row actions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBrand, setEditingBrand] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [actionBrand, setActionBrand] = useState(null);

  // Delete Confirm Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Debounce the search box. Only reset to page 1 when the text actually
  // changed — this used to fire 500 ms after every mount and bounce an admin
  // who had already clicked to page 2 back to page 1.
  useEffect(() => {
    if (searchTerm === debouncedSearch) return undefined;
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, debouncedSearch]);


  const loadBrands = useCallback(() => {
    dispatch(fetchAdminBrands({
      page: page + 1,
      limit: rowsPerPage,
      search: debouncedSearch
    }));
  }, [dispatch, page, rowsPerPage, debouncedSearch]);

  // Deleting the last row(s) of the last page left an empty page; step back.
  useEffect(() => {
    if (!loading && page > 0 && brands.length === 0 && total > 0) {
      setPage(Math.max(0, Math.ceil(total / rowsPerPage) - 1));
    }
  }, [loading, page, brands.length, total, rowsPerPage]);

  // After CREATING, show page 1 with no search: the list is newest-first, so
  // the new item is there. Reloading the current page (page 3, or a search)
  // made a just-created item look "not found".
  const showNewest = useCallback(() => {
    if (page === 0 && !searchTerm && !debouncedSearch) loadBrands();
    else { setSearchTerm(''); setDebouncedSearch(''); setPage(0); }
  }, [page, searchTerm, debouncedSearch, loadBrands]);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  // Parent-category dropdown: refresh once per visit (a category may have been
  // added or renamed since), not on every page/search change of this table.
  useEffect(() => {
    dispatch(fetchCategoryOptions());
  }, [dispatch]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = brands.map((n) => n._id);
      setSelectedIds(newSelecteds);
      return;
    }
    setSelectedIds([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedIds.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedIds, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedIds.slice(1));
    } else if (selectedIndex === selectedIds.length - 1) {
      newSelected = newSelected.concat(selectedIds.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedIds.slice(0, selectedIndex),
        selectedIds.slice(selectedIndex + 1),
      );
    }
    setSelectedIds(newSelected);
  };

  const handleMenuOpen = (event, brand) => {
    setMenuAnchorEl(event.currentTarget);
    setActionBrand(brand);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setActionBrand(null);
  };

  const handleEditClick = () => {
    setEditingBrand(actionBrand);
    setIsModalOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setMenuAnchorEl(null);
    setIsBulkDelete(false);
    setDeleteDialogOpen(true);
  };

  const handleBulkDeleteClick = () => {
    setIsBulkDelete(true);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (isBulkDelete) {
        const result = await dispatch(bulkDeleteAdminBrands(selectedIds));
        if (result.meta.requestStatus === 'fulfilled') {
          setSelectedIds([]);
          setSnackbar({ open: true, message: 'Selected brands deleted successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.payload || 'Failed to delete brands', severity: 'error' });
        }
      } else if (actionBrand) {
        const result = await dispatch(deleteAdminBrand(actionBrand._id));
        if (result.meta.requestStatus === 'fulfilled') {
          setSnackbar({ open: true, message: 'Brand deleted successfully', severity: 'success' });
        } else {
          setSnackbar({ open: true, message: result.payload || 'Failed to delete brand', severity: 'error' });
        }
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'An unexpected error occurred', severity: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setActionBrand(null);
      loadBrands();
    }
  };

  const isSelected = (id) => selectedIds.indexOf(id) !== -1;

  return (
    <Box>
      <PageHeader
        title="Brands"
        subtitle="Manage brand labels, manufacturer records and linked categories"
        count={total}
        countLabel="brands"
        action={
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => { setEditingBrand(null); setIsModalOpen(true); }}
          >
            New brand
          </Button>
        }
      />

      <Card sx={{ borderRadius: 3, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.6), zIndex: 10, backdropFilter: 'blur(2px)'
          }}>
            <CircularProgress thickness={4} size={36} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {/* Toolbar */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={(v) => setSearchTerm(v.trimStart())}
          searchPlaceholder="Search brand..."
          selectedCount={selectedIds.length}
          onBulkDelete={handleBulkDeleteClick}
          hasActiveFilters={Boolean(searchTerm)}
          onResetFilters={() => { setSearchTerm(''); setPage(0); }}
        >
          <ExportTableButton
            data={brands}
            total={total}
            onExportAll={async () => {
              const res = await axios.get('/admin/brand', {
                params: {
                  all: true,
                  search: debouncedSearch || undefined,
                },
              });
              return res.data.brands || [];
            }}
            columns={[
              { label: 'Brand Name', key: 'brand_name' },
              { label: 'Linked Category', value: (row) => row.categories?.category_name || 'N/A' },
              { label: 'Status', key: 'brand_status' },
            ]}
            filename="brands"
          />
        </FilterBar>

        {error ? (
          <Box sx={{ px: 3, pb: 3 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 480, sm: 600 } }}>
              <TableHead sx={{ backgroundColor: 'background.neutral' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      indeterminate={selectedIds.length > 0 && selectedIds.length < brands.length}
                      checked={brands.length > 0 && selectedIds.length === brands.length}
                      onChange={handleSelectAllClick}
                    />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Brand</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Linked Category</TableCell>
                  <TableCell sx={{ color: 'text.secondary', fontWeight: 700 }}>Status</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {brands.length > 0 ? brands.map((brand) => {
                  const isItemSelected = isSelected(brand._id);
                  return (
                    <TableRow key={brand._id} hover selected={isItemSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={isItemSelected}
                          onChange={(event) => handleClick(event, brand._id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            variant="rounded"
                            sx={{ width: 38, height: 38, bgcolor: avatarColor(brand.brand_name || ''), color: '#fff', fontSize: 14, fontWeight: 700 }}
                          >
                            {(brand.brand_name || '?').trim().charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{brand.brand_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {brand.categories ? brand.categories.category_name : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          status={brand.brand_status === 'Activated' ? 'active' : 'inactive'}
                          label={brand.brand_status || 'Disabled'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton aria-label="Row actions" size="small" onClick={(e) => handleMenuOpen(e, brand)}>
                          <MoreVertical size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }) : !loading && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ p: 0 }}>
                      <EmptyState
                        icon={Tag}
                        title="No brands found"
                        description="Try adjusting your search query or add a new brand."
                        actionLabel="Add Brand"
                        onAction={() => { setEditingBrand(null); setIsModalOpen(true); }}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: { xs: 1, sm: 2 } }}>
          <TablePagination
            component="div"
            count={total || 0}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25]}
            labelRowsPerPage={isMobile ? "" : "Rows per page:"}
            sx={{
              border: 'none',
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      </Card>

      {/* Row Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{ sx: { width: 140, borderRadius: 2 } }}
      >
        <MenuItem onClick={handleEditClick} sx={{ fontSize: '0.875rem', gap: 1 }}>
          <Edit size={16} /> Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main', fontSize: '0.875rem', gap: 1 }}>
          <Trash2 size={16} /> Delete
        </MenuItem>
      </Menu>

      {/* Create / Edit Modal */}
      <BrandModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        brand={editingBrand}
        categories={categories}
        onSuccess={() => {
          if (editingBrand) loadBrands(); else showNewest();
          setSnackbar({ open: true, message: `Brand ${editingBrand ? 'updated' : 'created'} successfully!`, severity: 'success' });
        }}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirm Delete"
        content={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedIds.length} brands? This action cannot be undone.`
            : `Are you sure you want to delete "${actionBrand?.brand_name || 'this brand'}"? This action cannot be undone.`
        }
        confirmText="Delete"
        severity="error"
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2, boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BrandsView;
